import { FC, useState, useContext, useRef, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Divider } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import CheckIcon from '@material-ui/icons/Check';
import RestoreIcon from '@material-ui/icons/Restore';
import { SubmitHandler, FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import BackButton from 'src/components/BackButton';
import Button from 'src/components/Button';
import ConfirmDialog from 'src/components/ConfirmDialog';
import { ConfirmDialogRef } from 'src/components/ConfirmDialog/interfaces';
import Autocomplete from 'src/components/Form/Autocomplete';
import CheckBox from 'src/components/Form/Checkbox';
import DateTimePicker from 'src/components/Form/DateTimePicker';
import InputFile from 'src/components/Form/InputFile';
import Select from 'src/components/Form/Select';
import { Item } from 'src/components/Form/Select/interfaces';
import TextField from 'src/components/Form/TextField';
import { IEditParams } from 'src/interfaces/IEditParams';
import { IBeneficiary } from 'src/interfaces/models';
import IPopup from 'src/interfaces/models/IPopup';
import PrivateContext from 'src/routes/Private/PrivateContext';
import api from 'src/services/api';
import { handleApiResponseErrors, showFormErrors } from 'src/utils/errors';
import { objectToFormData, objectToQuery } from 'src/utils/helpers';
import yupValidate from 'src/utils/yupValidate';
import {
  PopupStoreSchema,
  PopupUpdateSchema,
} from 'src/validators/Popup/save.schema';

import { Container } from './styles';

const openWhenItems: Item[] = [
  { key: 1, label: 'Sair da Página', value: 'leave' },
  { key: 2, label: 'Abrir a Página', value: 'load' },
  { key: 3, label: 'Após tempo', value: 'timeout' },
];
const typeItems: Item[] = [
  { key: 1, label: 'Exibição Padrão (Imagem)', value: 'default' },
  { key: 2, label: 'Exibir Vídeo', value: 'video' },
  { key: 3, label: 'Captura de Lead', value: 'lead' },
];

const Save: FC = () => {
  const { startLayoutLoading, stopLayoutLoading, layoutLoading } = useContext(
    PrivateContext,
  );
  const formRef = useRef<FormHandles>(null);
  const [popup, setPopup] = useState<IPopup>();
  const params = useParams<IEditParams>();
  const history = useHistory();
  const confirmDialogRef = useRef<ConfirmDialogRef>(null);
  const [beneficiaries, setBeneficiaries] = useState<IBeneficiary[]>([]);
  const [loading, setLoading] = useState(false);
  const [openWhen, setOpenWhen] = useState('');
  const [type, setType] = useState('');

  const yupSchema = params.id ? PopupUpdateSchema : PopupStoreSchema;

  useEffect(() => {
    const loadData = async () => {
      try {
        startLayoutLoading();

        const queryParams = objectToQuery({
          has_subsite: true,
          with_subsite: true,
          order_by: [
            { column: 'status', direction: 'asc' },
            { column: 'display_name', direction: 'asc' },
          ],
        });
        const { data } = await api.get<IBeneficiary[]>(
          `admin/beneficiaries${queryParams}`,
        );
        setBeneficiaries(data);

        if (params.id) {
          const response = await api.get(`admin/popups/${params.id}`);
          const popup = response.data as IPopup;

          setPopup(popup);
          formRef.current?.setData({
            ...popup,
            subsites_ids: popup.subsites?.map((subsite) => subsite.id),
          });
        }
      } catch (error) {
        handleApiResponseErrors(
          error.response,
          'Erro ao buscar dados.',
          yupSchema,
          formRef,
        );
      } finally {
        stopLayoutLoading();
      }
    };

    loadData();
  }, [params.id, startLayoutLoading, stopLayoutLoading, yupSchema]);

  const handleOnSubmit: SubmitHandler = async (unformData) => {
    try {
      startLayoutLoading();
      formRef.current?.setErrors({});

      const { success, data, errors } = await yupValidate(
        yupSchema,
        unformData,
      );

      if (!success) {
        return showFormErrors(errors, formRef);
      }

      const formData = objectToFormData(data);

      if (params.id) {
        await api.put(`/admin/popups/${params.id}`, formData);
      } else {
        const createResponse = await api.post('/admin/popups', formData);
        history.replace(`/popups/${createResponse.data.id}`);
      }

      toast.success('Dados salvos com sucesso!');
      history.goBack();
    } catch (error) {
      handleApiResponseErrors(error.response, 'Erro');
    } finally {
      stopLayoutLoading();
    }
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);

      if (popup?.status === 'active') {
        const isDelete = confirmDialogRef.current?.isDelete();
        let deleteConfig = {};
        if (isDelete) {
          deleteConfig = { data: { delete: true } };
        }

        await api.delete(`/admin/popups/${params.id}`, deleteConfig);

        toast.success(`Popup ${isDelete ? 'excluido' : 'desativado'}!`);
      } else {
        await api.post(`/admin/popups/${params.id}/restore`);
        toast.success('Popup restaurado!');
      }
      history.goBack();
    } catch (error) {
      handleApiResponseErrors(error.response, 'Erro ao desativar popup.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Form ref={formRef} onSubmit={handleOnSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container alignItems="center">
              <BackButton />

              <Typography variant="h5">
                {params.id ? 'Editar' : 'Novo'} Popup
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField name="description" label="Descrição" required />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Select
                      name="type"
                      label="Tipo de Exibição"
                      items={typeItems}
                      required
                      onChange={(val) => setType(val)}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Select
                      name="open_when"
                      label="Exibir quando"
                      items={openWhenItems}
                      required
                      onChange={(val) => setOpenWhen(val)}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    hidden={openWhen !== 'timeout'}
                  >
                    <TextField
                      name="timeout"
                      label="Segundos de Espera"
                      type="number"
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <CheckBox
                      name="show_always"
                      label="Ativar exibição em loop"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption">
                      Intervalo de data em que a Popup ficará em exibição ou
                      deixe os campos em branco para período indeterminado:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <DateTimePicker
                      name="start_at"
                      label="Início"
                      valueAsDate
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <DateTimePicker name="end_at" label="Fim" valueAsDate />
                  </Grid>

                  <Grid item xs={12} hidden={type === 'lead'}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12} sm={4} hidden={type !== 'default'}>
                    <InputFile
                      name="image"
                      accept="image/*"
                      label="Imagem do Popup"
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm>
                    <Grid container spacing={1}>
                      <Grid item xs={12} hidden={type !== 'video'}>
                        <TextField
                          name="video_url"
                          label="Link de incorporação do vídeo"
                          placeholder="https://link-do-video.com.br"
                          required
                        />
                      </Grid>

                      <Grid item xs={12} hidden={type === 'lead'}>
                        <TextField
                          name="url"
                          label="Link de ação ao clicar"
                          placeholder="https://site.com.br"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" display="block">
                      Visibilidade da Popup:
                    </Typography>

                    <CheckBox
                      name="show_collaborators"
                      label="Exibir no IFEPAF"
                    />
                    <CheckBox
                      name="show_subscribers"
                      label="Exibir no IFBOLSAS"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      name="subsites_ids"
                      label="Exibição em Subsites"
                      multiple
                      options={beneficiaries.map((beneficiary) => ({
                        key: beneficiary.id,
                        label: `${beneficiary.display_name} (${beneficiary.subsite?.slug})`,
                        value: beneficiary.subsite?.id,
                        disabled: beneficiary.status !== 'active',
                      }))}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Grid container justify="space-between">
              {popup?.status === 'active' ? (
                <Button
                  type="button"
                  loading={loading}
                  // startIcon={<CloseIcon />}
                  variant="contained"
                  color="secondary"
                  disabled={!params.id}
                  onClick={() => confirmDialogRef.current?.show()}
                >
                  Desativar
                </Button>
              ) : (
                <Button
                  type="button"
                  loading={loading}
                  startIcon={<RestoreIcon />}
                  color="primary"
                  disabled={!params.id}
                  onClick={() => confirmDialogRef.current?.show()}
                >
                  Restaurar
                </Button>
              )}
              <Button
                type="submit"
                loading={layoutLoading}
                startIcon={<CheckIcon />}
                variant="contained"
                color="primary"
              >
                Salvar
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Form>

      <ConfirmDialog
        ref={confirmDialogRef}
        title={`${
          popup?.status === 'active' ? 'Desativar' : 'Restaurar'
        } popup`}
        description="Confirma esta ação?"
        confirmColor={popup?.status === 'active' ? 'secondary' : 'primary'}
        onConfirm={handleConfirm}
        canDelete={popup?.status === 'active'}
      />
    </Container>
  );
};

export default Save;
